import React from 'react';
import { Box, CircularProgress, Alert, Fade } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { ReviewViewModel } from '../../configuration/api/apiSchema';
import ReviewItem from './ReviewItem';

interface ReviewsListProps {
  reviews: ReviewViewModel[];
  loading: boolean;
  error: string | null;
  totalCount: number;
  onPageChange?: (page: number) => void;
  onRespond?: (reviewId: string, response: string) => void;
  showActions?: boolean;
  emptyMessage?: string;
  title?: string;
  currentPage?: number;
}

const ReviewsList: React.FC<ReviewsListProps> = ({
  reviews,
  loading,
  error,
  totalCount,
  onPageChange,
  onRespond,
  showActions = false,
  emptyMessage = 'No reviews found',
  title = 'Reviews',
  currentPage,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        padding: '0px',
        gap: '24px',
        width: '100%',
      }}
    >
      {loading ? (
        <Box display='flex' justifyContent='center' width='100%' my={4}>
          <CircularProgress sx={{ color: '#00FFFF' }} />
        </Box>
      ) : error ? (
        <Alert severity='error' sx={{ mb: 2, width: '100%' }}>
          {error}
        </Alert>
      ) : reviews.length === 0 ? (
        <Fade in={true} timeout={500}>
          <Box
            display='flex'
            justifyContent='center'
            alignItems='center'
            width='100%'
            height='200px'
            sx={{
              background: isDark ? '#1A1A1A' : '#E5E5DC',
              boxShadow:
                '5px 5px 24px -11.7px rgba(0, 0, 0, 0.2), -7.54px -7.54px 8.66px rgba(255, 255, 255, 0.2), 14.15px 14.1509px 22.64px -5.37px rgba(0, 0, 0, 0.1), -2.82453px -2.82453px 8.49057px rgba(255, 255, 255, 0.3)',
              borderRadius: '32px',
            }}
          >
            <Box
              sx={{
                fontFamily: 'Roboto',
                fontStyle: 'normal',
                fontWeight: 400,
                fontSize: '16px',
                letterSpacing: '0.15px',
                color: isDark ? '#D5D5D5' : '#585858',
              }}
            >
              {emptyMessage}
            </Box>
          </Box>
        </Fade>
      ) : (
        <Fade in={true} timeout={500}>
          <Box sx={{ width: '100%' }}>
            {reviews.map((review) => (
              <ReviewItem
                key={review.id}
                review={review}
                showActions={showActions}
              />
            ))}
          </Box>
        </Fade>
      )}
    </Box>
  );
};

export default ReviewsList;
